import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { PRODUCTS, CATEGORIES } from '../data/mock';
import { Search, SlidersHorizontal } from 'lucide-react';
import Seo, { SITE_URL } from '../components/Seo';

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const categoryFromPath = CATEGORIES.find(c => c.slug === categorySlug && c.id !== 'all')?.id;
  const catParam = categoryFromPath || params.get('cat') || 'all';
  const qParam = params.get('q') || '';
  const [sort, setSort] = useState('default');
  const [query, setQuery] = useState(qParam);

  const filtered = useMemo(() => {
    let list = PRODUCTS;
    if (catParam !== 'all') list = list.filter(p => p.category === catParam);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.short.toLowerCase().includes(q));
    }
    if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'name-desc') list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    return list;
  }, [catParam, query, sort]);

  const currentCat = CATEGORIES.find(c => c.id === catParam) || CATEGORIES[0];
  const categoryPath = currentCat.id === 'all' ? '/sklep' : `/kategoria/${currentCat.slug}`;
  const pageTitle = currentCat.id === 'all' ? 'Katalog produktów | AlphaPro Genetics' : `${currentCat.name} | AlphaPro Genetics`;
  const pageDescription = currentCat.id === 'all'
    ? 'Przeglądaj katalog produktów AlphaPro Genetics według kategorii. Sprawdź dostępne produkty i ich opisy.'
    : `Kategoria ${currentCat.name} w katalogu AlphaPro Genetics. Przeglądaj dostępne produkty i ich opisy.`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <Seo
        title={pageTitle}
        description={pageDescription}
        path={categoryPath}
        noIndex={Boolean(qParam)}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: currentCat.id === 'all' ? 'Katalog produktów AlphaPro Genetics' : currentCat.name,
          url: `${SITE_URL}${categoryPath}`,
          inLanguage: 'pl-PL',
        }}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-3xl p-8 mb-8 border border-blue-100">
        <div className="flex items-center text-xs text-slate-500 mb-2">
          <span>Strona Główna</span>
          <span className="mx-2">/</span>
          <span className="text-blue-600 font-semibold">{currentCat.name}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{currentCat.name}</h1>
        <p className="text-slate-600 mt-2">Znaleziono {filtered.length} produktów</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white rounded-2xl border border-blue-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-blue-600" />
              <h4 className="font-semibold text-slate-900 text-sm">Szukaj</h4>
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nazwa produktu..."
              className="w-full px-4 py-2.5 rounded-full bg-blue-50/70 border border-blue-100 focus:bg-white focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-100 text-sm transition"
            />
          </div>

          <div className="bg-white rounded-2xl border border-blue-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <SlidersHorizontal className="w-4 h-4 text-blue-600" />
              <h4 className="font-semibold text-slate-900 text-sm">Kategorie</h4>
            </div>
            <ul className="space-y-1">
              {CATEGORIES.map(c => (
                <li key={c.id}>
                  <button
                    onClick={() => {
                      if (c.id === 'all') navigate('/sklep');
                      else navigate(`/kategoria/${c.slug}`);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${catParam === c.id ? 'bg-blue-600 text-white font-semibold' : 'text-slate-700 hover:bg-blue-50'}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-sky-500 rounded-2xl p-6 text-white">
            <div className="text-xs font-semibold uppercase tracking-widest opacity-80 mb-2">Anonimowość</div>
            <h4 className="font-bold text-lg mb-2">Kontakt przez Telegram</h4>
            <p className="text-sm text-blue-100 mb-4">Po wyborze produktu napisz do nas - żadnych danych na stronie.</p>
            <a href="https://t.me/+9A5CpRihCfNjMzc0" target="_blank" rel="noreferrer" className="inline-block bg-white text-blue-600 hover:bg-blue-50 px-5 py-2 rounded-full font-semibold text-sm transition">Otwórz Telegram</a>
          </div>
        </aside>

        {/* Main */}
        <div>
          <div className="flex items-center justify-between mb-5 bg-white rounded-2xl border border-blue-100 p-4">
            <div className="text-sm text-slate-600">{filtered.length} produktów</div>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-4 py-2 rounded-full bg-blue-50 border border-blue-100 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300">
              <option value="default">Sortowanie domyślne</option>
              <option value="name">Nazwa: A-Z</option>
              <option value="name-desc">Nazwa: Z-A</option>
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-blue-100 p-16 text-center">
              <div className="text-slate-500">Brak produktów spełniających kryteria</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {filtered.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
